import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ChevronDown, Compass, History,
  Loader2, RotateCw, Scaling,
} from 'lucide-react';
// The run's own measured box, which mmScale falls back to — see builtBoxOf. Asked here as
// well as by the labels, because the drill and the hole readings must be in the millimetres
// the viewer is drawing.
import { useLanguage } from '../../contexts/LanguageContext';
import { defaultPicks, useAskPictures, useProductShots } from './askPictures';
import { CallBadge } from './CallBadge';
import { tipSection } from './ReasoningBadge';
import { renderAxisViews } from './meshAxisViews';
import { directionLabel } from './viewDirections';
import { paintMaskedShot } from './maskedShot';
import { modelLabel } from './models';
import { isWrittenRun } from './openaiBlenderApi';
import { setOrientShot, shotIsOn, showsPart, useOrientShots } from './orientShots';
import { OrientViews } from './OrientViews';
import { setOrientAnswer, setOrientRunning } from './orientAnswers';
import { clock, useWaited } from './waitClock';
import { axisFit, type AxisFit, type Extent } from './meshAnnotations';
import { pictureSrc, type PictureRef } from './pictureRef';
import {
  getParts, groupOfCategory, type PartItem,
} from './partsRun';
import { setModelProfile } from './partModelSize';
import type { PartProfile } from './partProfile';
import { useCategories } from './catalogCategories';
import { RepairAskModal } from './RepairAskModal';
import { MeshRotateModal } from './MeshRotateModal';
import { MeshProposalCard } from './MeshProposalCard';
import { MeshProposalRail } from './MeshProposalRail';
import { cssRgb } from './sampleColour';
import { faceKey, type Face } from './faces';
import { Band } from './Band';
import {
  cropRings, toggleListingCrop, useSubmodelListingCrops,
} from './listingCropsRun';
import {
  adviseRepair, deleteProposal, editsOf, falError, fetchAdviceContext,
  dropEdit, dropEdits, fetchProposals, isTrueScale, meshSrc, meshStamp, orientJob, orientPrompt,
  repairComplaints, runScript,
  type Job3D, type MeshEdit, type MeshProposal,
  type OrientAnswer,
  type OrientKind,
  type RepairAdvice,
} from './threeDApi';


// Everything you can DO to the mesh that fills the screen behind it.
//
// It all lives under the expanded viewer and nowhere else, because every one of these is a
// judgement about geometry and a judgement taken from a thumbnail is one taken without
// looking. What you have to read to decide is the arrow's two numbers, the drawing's and
// the mesh's, side by side — and that is not legible in a strip across a page.
//
// A ROW PER KIND OF THING, each centred under the mesh. The two MENUS first — scaling and
// the modifications, everything you can ask for by naming it — then the row where you ask
// for it in words instead, then what the model proposed, then Undo.
//
// SCALING IS A MENU, not three buttons in a row. They were three because there are three
// axes, but you press ONE of them: the disagreement is per axis (this chair's width is
// exact and its height is 14% over), so the row was three-quarters wrong at all times and
// took a row's width to say so. A menu says the same thing in one word and stacks the
// choice underneath, where the two numbers that decide it can be read down a column.
//
// THE TWO MENUS SHARE A ROW, and the sentence has the next one. Naming a modification and
// describing a fault are the same decision arrived at two ways, and they sat on one line
// for that reason — but a menu beside a box three lines tall reads as a label for the box,
// and it left the middle of the panel empty while two buttons stacked down the left.
//
// THE SENTENCE IS WRITTEN ELSEWHERE now — pressing its box opens RepairAskModal. The
// question has two halves, the words and the DRAWINGS they point at, and a row under a
// mesh has room for the first only. What stays here is a preview of what would be sent.
//
// All of it was spread across one row with justify-between for a while. That put unrelated
// groups on one line held apart by nothing but the width of the modal, and the scale
// buttons wrapped inside their own group while the rest sat level with the leftovers.
//
// EVERYTHING IS WHITE, borders and text alike, and all of it well off pure: 60% on the
// contours, 80% on the type. Full white on a near-black panel is louder than the mesh it
// surrounds, and these are controls AROUND the thing being looked at — they should be
// legible and then get out of the way.
//
// One opacity for all of them, which is the part that matters. A disabled control used to
// drop to 40% and read as grey, which put two visual languages in one row and made "there
// is no measurement to scale to" look like a rendering fault. It says that in its tooltip,
// and the cursor says it too.
//
// The delete is NOT here. It went to the modal's own top-left corner, opposite the close:
// it is the only control down here that acts on the run rather than on the geometry, and
// it was the one thing in a row of make-it-better buttons that made nothing better.

// The three axes, named as the gizmo in the corner of the viewer names them: X across, Y
// into the picture, Z up. The word beside each is what that axis MEANS on the part, because
// "scale on Z" and "scale the height" are the same instruction and only one of them is
// obvious.
//
// Y AND Z WERE THE OTHER WAY ROUND HERE, from when this viewer rendered Y-up. It is Z-up
// now, so the words were on the wrong two buttons — and the arithmetic behind them was
// crossed the same way, which is what made "scale on Z" fetch the depth and apply it to the
// height. See the note on SWAP in meshAnnotations.
const SCALE_AXES = [
  { axis: 'x', label: 'notice.3d.width' },
  { axis: 'y', label: 'notice.3d.depth' },
  { axis: 'z', label: 'notice.3d.height' },
] as const;

// One button, in the shape every control down here shares. Written once because six of
// them differed only in their icon and their word, and the fourth copy of a class string
// this long is where one of them quietly stops matching.
const BTN = 'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 '
  + 'text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed';
// Exported, because a caller can hand one more button to this row (see `tools`) and it has
// to be the same button. A copy of this string somewhere else is the fifth copy, and the
// comment above BTN is about where the fourth one quietly stopped matching.
// LIGHT FIRST, DARK AS THE VARIANT — which is the way round every other panel in this app
// is written, and this file was the exception. It was authored against a dark surface and
// then hung in MeshModal's footer, whose panel is bg-white: white text on white. What
// vanished was everything wearing a word — the whole control row, the menu it opens, and
// the line saying you are looking at an earlier mesh — while the icons, being coloured,
// stayed. A panel of invisible buttons reads as a panel that failed to load.
export const PLAIN = `${BTN} border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600 `
  + 'disabled:hover:border-slate-300 disabled:hover:text-slate-600 '
  + ''
  + '';

// What an edit is CALLED, for the button that would take it back. A rescale says which
// axis it fitted, because a mesh that has been scaled on two of them has two entries that
// are otherwise the same word.
// EXPORTED, because the retouch panel now keeps a rail of its own — the corrections of one
// sitting, before any of them is written — and the entry it draws there has to read the same
// as the entry the same work leaves behind afterwards. A second naming function is a second
// vocabulary for one set of decisions.
export function editName(e: MeshEdit, t: (k: string) => string): string {
  // A written transformation is called whatever it was named. There is no other word for
  // it: "script" would be the same word on every one of them, and what you are about to
  // undo is "shorten the legs", not the fact that it was code.
  if (e.op === 'script') return (e.name || t('notice.3d.transformation')).toLowerCase();
  if (e.op === 'rescale') {
    return `${t('notice.3d.scaleOn')} ${(e.axis || '').toUpperCase()}`.trim().toLowerCase();
  }
  // A NAMED MATERIAL SAYS ITS NAME. "Undo colour" is true of every one of them and
  // tells you nothing; "undo felt, grey" is the decision being taken back.
  //
  // AND WHETHER THE GRAIN SURVIVED IT, when it did. Two entries a minute apart, the same
  // material and the same three digits, are the same decision taken twice unless the rail
  // says which of them kept the mesh's own figure — and that is exactly the pair somebody
  // makes while deciding which they prefer. Only when it did: "flat" on every colour ever
  // applied to a converted CAD part is a word that never varies and so never informs.
  // The wood it was given, named for what it is rather than for the operation: "grained"
  // says what happened to somebody who is looking at the rail to find the entry to undo.
  if (e.op === 'grain') return t('notice.3d.grained').toLowerCase();
  if (e.op === 'colour') {
    const what = (e.material || t('notice.3d.colour')).toLowerCase();
    return e.grain ? `${what} · ${t('notice.3d.colourGrainMark')}` : what;
  }
  // A re-turn is called by WHAT IT CHANGED — "head across 14 mm" — and not by the fact
  // that it was a re-turn. Every one of them was "new measurements", which named the kind
  // of edit rather than the edit, so a rail of them said nothing at all and a part adjusted
  // twice had two entries you could only tell apart by pressing them. The words come off
  // the server with the edit; the old label is what an entry from before that falls back to.
  if (e.op === 'lathe') return (e.what || t('notice.3d.latheEdit')).toLowerCase();
  // HOW MANY REFLECTIONS WERE TAKEN OUT. One entry is "a reflection"; three at once is a
  // different decision, and a rail that called both the same would have you open one to
  // find out which. The count is the whole of what varies between them.
  if (e.op === 'retouch') {
    const n = e.spots?.length ?? 0;
    return n > 1
      ? `${n} ${t('notice.3d.retouchEditMany')}`.toLowerCase()
      : t('notice.3d.retouchEdit').toLowerCase();
  }
  // A FACE BROUGHT TO THE COLOUR OF THE REST. Named for what it did to the part rather
  // than for the arithmetic: "level" is what the code calls it and says nothing to anybody
  // reading a rail of decisions.
  if (e.op === 'level') return t('notice.3d.levelEdit').toLowerCase();
  // A SITTING'S WORTH, named by what went into it. One panel, several corrections, one
  // entry — so the rail has to say which kinds, or "corrections to the picture" is what
  // every afternoon of this work looks like afterwards.
  if (e.op === 'texture') {
    const faces = e.faces ?? 0;
    const spots = e.marks ?? 0;
    const parts: string[] = [];
    if (faces === 1) parts.push(t('notice.3d.levelEdit'));
    else if (faces > 1) parts.push(`${faces} ${t('notice.3d.levelEditMany')}`);
    if (spots === 1) parts.push(t('notice.3d.retouchEdit'));
    else if (spots > 1) parts.push(`${spots} ${t('notice.3d.retouchEditMany')}`);
    return (parts.join(' · ') || t('notice.3d.textureEdit')).toLowerCase();
  }
  // A TURN IS NAMED BY WHAT MOVED. "Orientation" is true of every one of them and says
  // nothing; what somebody wants back off the rail is which face was wrong — "front was its
  // left" is a claim you can look at the mesh and disagree with, and disagreeing is the
  // whole reason the entry is undoable.
  if (e.op === 'orient') {
    // A HAND NAMES FACES TOO, now that it is asked for two of them rather than for an axis and
    // a right angle — so the rail says which one moved, and says whose hand it was. Turns
    // pressed before that are the entries with no `front` on them: "turned by hand" is the
    // true and whole of what was recorded about those.
    const face = e.front
      && t(faceKey(e.front)).toLowerCase();
    if (e.hand) {
      return (face
        ? t('notice.3d.orientEditHandFace').replace('{f}', face)
        : t('notice.3d.orientEditHand')).toLowerCase();
    }
    return face
      ? t('notice.3d.orientEdit').replace('{f}', face)
      : t('notice.3d.orientEditPlain').toLowerCase();
  }
  // A HOLE IS CALLED BY WHAT WAS DONE TO IT, THEN WHERE, THEN HOW BIG — "hole drilled, Front
  // face, 8 mm through" — because a part usually takes several and "drill" on all of them is a
  // rail of identical entries where the only way to tell one from another is to undo it.
  //
  // THE FACE KEEPS ITS CAPITAL, alone among the words this function assembles: it is the name
  // written on that side of the box in the viewer, and a reader checking the entry against the
  // mesh is looking for that exact word.
  // A BATCH OF THEM, one entry — named by the count, as Cut & fill all is.
  if (e.op === 'drill_all') return t('notice.3d.drillAllEdit').replace('{n}', String(e.holes ?? 0));
  if (e.op === 'drill') {
    return `${t(e.through ? 'notice.3d.drillEditThrough' : 'notice.3d.drillEditBlind')}`
      .replace('{mm}', String(e.diameter ?? ''))
      .replace('{face}', t(faceKey((e.face || 'front') as Face)))
      .replace('{depth}', String(e.depth ?? ''));
  }
  // AND A CLOSING IS NAMED BY WHETHER IT CLOSED. "Closed the surface" on an attempt that
  // ended with a rim it refused to touch says the opposite of what happened, and that is
  // exactly the entry somebody would go looking for afterwards.
  if (e.op === 'close') {
    return t(e.closed ? 'notice.3d.closeEdit' : 'notice.3d.closeEditShort').toLowerCase();
  }
  // AND A FILL BY THE HOLE THAT WENT, in the same words the drill names one it made — a part
  // whose reconstructed holes were filled and re-bored has both entries on one rail, and they
  // have to read as the pair they are.
  // TWO ENTRIES NOW, because a fill is two presses that each rewrite the mesh: the ball came
  // out, and then the wound was closed. 'fill' is what the single press left behind and is
  // still on rails written before this.
  // ONE PRESS OF CUT & FILL ALL, which is one entry however many holes it went through —
  // named by the count, since that is the thing that would be undone.
  if (e.op === 'fill_all') {
    return t('notice.3d.fillAllEdit').replace('{n}', String(e.holes ?? 1)).toLowerCase();
  }
  if (e.op === 'fill' || e.op === 'fill_cut' || e.op === 'fill_close'
      || e.op === 'fill_cap') {
    return t(e.op === 'fill_cut' ? 'notice.3d.fillCutEdit'
      : (e.op === 'fill_close' || e.op === 'fill_cap') ? 'notice.3d.fillCloseEdit'
      : 'notice.3d.fillEdit')
      .replace('{mm}', String(e.diameter ?? ''))
      .replace('{face}', t(faceKey((e.face || 'front') as Face)));
  }
  // A DELETION IS NAMED BY WHAT WENT, not by what stayed. "Kept 1 piece" is the same
  // sentence whether one copy was dropped or nine, and what somebody looks for on the rail
  // afterwards is the thing they took away.
  if (e.op === 'keep') {
    const n = e.dropped ?? 0;
    return `${n} ${t(n === 1 ? 'notice.3d.keepEditOne' : 'notice.3d.keepEditMany')}`.toLowerCase();
  }
  if (e.op === 'fill_end_slot') return t('notice.3d.fillSlot').toLowerCase();
  if (e.op === 'cut_drive_recess') return t('notice.3d.cutCross').toLowerCase();
  // WHICH PIECE WENT ONTO WHICH MATERIAL — "backrest and rear legs frame → tissu, beige".
  if (e.op === 'pieces') {
    return (e.pieces ?? []).map((p) => `${p.piece} → ${p.material}`).join(' · ').toLowerCase()
      || t('notice.3d.piecesEdit').toLowerCase();
  }
  // NAMED BY WHAT IT IS, or not named at all. This used to end in a two-way choice between
  // the only repairs there were, so every op the list had not heard of came out as "cut a
  // cross recess" — and a re-turn of the measurements offered to undo a recess nobody had
  // cut. A word that is merely the last branch of an if is a wrong answer with a straight
  // face; the op itself is at least true.
  return e.op;
}

// One choice on a menu: what it is called, and what pressing it does.
type MenuItem = {
  key: string;
  icon: ReactNode;
  label: ReactNode;
  // Why you would press it — or, on a disabled one, why you cannot. The scale items carry
  // the whole argument for pressing them here: the mesh's number and the drawing's.
  title?: string;
  disabled?: boolean;
  onPick: () => void;
};

// A list of things to choose from, under the button that names the choice. Written once
// because there are two of them — which modification, and which axis — and they are the
// same object: a labelled button, a stack under it, one press and it shuts.
//
// OPENS DOWNWARD, over whatever is under it, which is what a menu does. The repair menu
// opened upward while it was the last row in the modal and its bottom edge was the panel's.
//
// ESCAPE CLOSES THE MENU, not the modal behind it — and that needs the CAPTURE phase,
// because MeshModal listens on the document too and would otherwise take the whole dialog
// away when you meant to dismiss a two-item list. First listener down the tree wins, and
// capture is what puts this one first.
function Menu({ icon, label, disabled, items }: {
  icon: ReactNode;
  label: ReactNode;
  disabled?: boolean;
  items: MenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className={PLAIN}
      >
        {icon}
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          // CENTRED ON THE BUTTON THAT OPENED IT, not hung from its left edge. These lists
          // are much wider than their buttons — a material names three numbers — so a
          // left-aligned panel grew off to one side and read as belonging to whatever it
          // happened to cover rather than to the button above it.
          //
          // The row of buttons is centred and wraps, which is what makes this safe: a
          // centred panel only leaves the modal if its button is near an edge, and none of
          // them is.
          className="absolute left-1/2 top-full z-20 mt-1.5 w-max -translate-x-1/2 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-xl"
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              title={it.title}
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onPick(); }}
              // One opacity for all of them, as on the buttons outside: a disabled item
              // that dropped to grey would put two visual languages in one list and make
              // "there is no measurement to scale to" look like a rendering fault. It says
              // that in its tooltip, and the cursor says it too.
              className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[12.5px] font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600"
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MeshControls({
  job, docId, partAt, size, profile, dims, category, rescaling, onRescale, onChanged,
  viewingAt = null, onViewAt,
}: {
  job: Job3D;
  // The manual being worked on. Its listing photographs are what a COMPONENT can be shown
  // beside its drawing — where the part sits in the finished thing.
  docId: string | null;
  // WHICH PART this mesh is, as its index in the notice's parts list — which is how every
  // per-part answer in the phase is keyed, the listing crops among them. Null where the
  // caller does not know (the crops menu then offers the generated photographs alone).
  partAt?: number | null;
  // The mesh's extents in the file's own units, as the viewer measured them. Null until it
  // has loaded, which is what greys the scale buttons rather than letting one be pressed
  // against a box nobody measured.
  size: Extent | null;
  // AND WHAT SHAPE THE MESH IS INSIDE THAT BOX — see partProfile. Null while there is none,
  // which is a part nobody has opened yet and every record written before this existed; every
  // hole is then placed against the box, as they all were.
  profile: PartProfile | null;
  dims: PartItem['size'];
  // The part's category: which pictures a question about it is asked with, and which
  // orientation question that is.
  category: string | null;
  // The part's own photographs, which is where a colour comes from. Empty is why the
  // colour control is not offered: there is nothing to sample.
  rescaling: boolean;
  onRescale: (axis: 'x' | 'y' | 'z', fit: AxisFit) => void;
  // The same run, with a new mesh under it. Not a new row: an edit here keeps the id, so
  // the caller patches its list rather than refetching one.
  onChanged: (job: Job3D) => void;
  // WHICH STATE THE VIEWER IS SHOWING, as a count of edits, or null for the live mesh.
  // Owned by the caller because the viewer is: this only says which dot to ring and
  // whether to offer the way back.
  viewingAt?: number | null;
  // Go and look at an earlier state, or at the live mesh again (null). Absent means the
  // caller has no viewer to point anywhere, and the dots then only open their proposals.
  onViewAt?: (at: number | null) => void;
}) {
  const { t } = useLanguage();


  // WHAT THE PART'S MODEL MEASURES is not written here: it is the Parts DB model's, measured
  // whenever the manual is open (see ModelSizeSync). This viewer shows whatever mesh it is handed.

  // AND THE SHAPE INSIDE IT, kept the same way and for the same reason — Phase 4's reading
  // places the same bores on the same part and has no mesh of its own to ask. Written here
  // rather than by the viewer, because this is where a mesh is tied to a PART: the viewer
  // draws a file and does not know whose.
  useEffect(() => {
    if (partAt == null || !docId || !profile) return;
    setModelProfile(docId, partAt, profile);
  }, [docId, partAt, profile]);

  // THE CATEGORY ONTOLOGY, up here rather than beside its other reader, because the hole
  // styles below are read out of it: it arrives from the server after this panel does, and a
  // memo that did not depend on it would compute its answers once, before it landed, and never
  // again. See holeStyleOf, which answers null until it is here.
  const cats = useCategories();

  // ONE busy key across every control here, holding the name of what is running. Several
  // of these overwrite the same file, and two overlapping edits would each undo half of
  // the other — the second would be computed from the mesh the first had already replaced.
  const [busy, setBusy] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  // WHAT THE LAST ORIENTATION CALL SAID, when it did not turn anything.
  //
  // A turn that happened writes its reasoning onto the edit and is read back off the run
  // from then on — see `orientSaid` below, which prefers that. This holds the other case:
  // a mesh already the right way round leaves no edit, so its answer exists nowhere but
  // here, and it is the commonest answer a working button gives.
  const [orientNow, setOrientNow] = useState<OrientAnswer | null>(null);
  // Whether the turn-it-by-hand dialog is up. Its own state and not a mode of `busy`: it
  // holds nothing, computes nothing, and every press inside it is a whole edit of its own.
  const [turning, setTurning] = useState(false);
  // What is wrong, in the user's words. The instruction and the drawing only work
  // together: "the cross at this end is missing" POINTS AT the schematic, and neither half
  // means anything to a model that was handed only the other.
  const [instruction, setInstruction] = useState('');
  const [advice, setAdvice] = useState<RepairAdvice | null>(null);
  // Everything ever proposed for THIS mesh, newest first, from the server — so a refresh
  // comes back to it, renders included: a proposal carries the pictures it was made from.
  // `advice` is only what the newest call reported that a row does not keep — whether it
  // had a drawing to compare against.
  const [proposals, setProposals] = useState<MeshProposal[]>([]);
  // What one of these usually costs in seconds, across every mesh — the number the clock
  // counts toward while the model is writing. Null until something has been timed.
  const [average, setAverage] = useState<number | null>(null);
  // WHICH dot is open. Null means nobody has pressed one, which opens the newest — a mesh
  // you have just asked about opens on the answer, and one you are coming back to opens on
  // the last thing anybody proposed for it. -1 is "closed on purpose", which is a different
  // state and the reason this is not a boolean.
  const [openId, setOpenId] = useState<number | null>(null);
  // WHERE THIS PART WAS FOUND ON THE ASSEMBLED PRODUCT — the listing crops, per part.
  const { byPart: listingCrops } = useSubmodelListingCrops(docId ?? '');
  useEffect(() => {
    let alive = true;
    void fetchProposals(job.id)
      .then((got) => {
        if (!alive) return;
        setProposals(got.proposals);
        setAverage(got.averageSeconds);
      })
      // Silent. The controls all work without a history, and a mesh nobody has asked
      // about has none anyway.
      .catch(() => {});
    return () => { alive = false; };
  }, [job.id]);

  const forget = async (id: number) => {
    setProposals((list) => list.filter((x) => x.id !== id));
    if (advice?.proposal.id === id) setAdvice(null);
    // Back to the newest, rather than to a card whose dot has just left the rail: `openId`
    // naming a proposal that no longer exists shows nothing at all, which reads as the
    // history having been deleted rather than one row of it.
    setOpenId((was) => (was === id ? null : was));
    await deleteProposal(id).catch(() => {});
  };
  // Whether the box where both halves are written is open, and — when it was opened from a
  // proposal rather than from the row of menus — WHICH transformation the next question is
  // about. The two are different requests and the difference is not in the sentence: "make
  // it 2 mm deeper" is a correction to one particular thing, and only the person asking
  // knows which. So it is a press, not an inference.
  const [asking, setAsking] = useState(false);
  const [basedOn, setBasedOn] = useState<MeshProposal | null>(null);
  // The pictures there are to show the model, and which of them go with the question.
  //
  // NULL MEANS NOBODY HAS CHOSEN, which is not the same as choosing none: it falls back to
  // the default set. Kept as null rather than filled in by an effect, so the default
  // follows the catalog in whenever it lands instead of freezing whatever was known one
  // render earlier.
  const groups = useAskPictures(docId, job.itemId, category);
  const [chosen, setChosen] = useState<PictureRef[] | null>(null);
  const picked = chosen ?? defaultPicks(groups);

  // WHERE THIS PART IS ON THE PHOTOGRAPHS — the crops Phase 1 found, in the order they are
  // stored (which is the order toggleListingCrop counts in).
  //
  // Only the ones nobody has struck through go to the model. A crop somebody looked at and
  // rejected is not weak evidence, it is a mask on the wrong object, and a model told "this
  // IS the part" will bend the rest of its answer to fit it.
  //
  // What to call the part on a mask drawn over a photograph — the same name the parts table
  // writes on it, so the two pictures of one crop say one thing.
  const partName = (docId && partAt != null ? getParts(docId)[partAt]?.name : '') || '';
  const myCrops = useMemo(
    () => (partAt == null ? [] : listingCrops[partAt] ?? []),
    [listingCrops, partAt],
  );
  // WHICH PICTURES THIS PART WAS FOUND IN, which is now half of what decides the set below —
  // struck-through crops included, because a picture whose every crop was rejected is still
  // a picture somebody has ruled on, and the ruling is what turns its toggle off rather than
  // what hides it. See orientShots.
  const showing = useMemo(() => new Set(myCrops.map((c) => c.url)), [myCrops]);

  // THE PICTURES OF THE ASSEMBLED PRODUCT the orientation question is asked with, each
  // carrying the side it shows. Empty for hardware and for a notice whose listing nobody
  // has read, which is what hides the button: there is nothing to match the mesh against.
  const productShots = useProductShots(docId, category, showing);
  // WHAT THIS PART IS, which decides which orientation question is asked about it — and, with
  // it, whether the question can be asked at all without a photograph. See OrientKind.
  //
  // The part's own `tool` flag first, then the ontology. A tool is a tool however it was
  // filed: the flag is set by the phase that read the manual, and a spanner in a category
  // nobody has classified would otherwise be asked about as though it were a chair leg.
  // Computed on every render rather than memoized: it is a walk up a category chain of two or
  // three entries, and the `tool` flag it reads first comes out of a store this component does
  // not subscribe to — so a memo keyed by the props would hold yesterday's answer the one time
  // the flag is what changed. The same reason `partName` above reads getParts straight.
  const kind: OrientKind = ((): OrientKind => {
    if ((docId && partAt != null ? getParts(docId)[partAt] : undefined)?.tool) return 'tool';
    const group = groupOfCategory(category, cats);
    return group === 'Components' ? 'component' : group === 'Tools' ? 'tool' : 'hardware';
  })();
  // A SCREW BELONGS TO NOTHING, so there is nothing to match it against and nothing missing
  // when there are no photographs. This is what lets the model's half of the menu stand for
  // hardware, where for a component an empty picture set means the question cannot be asked.
  const solo = kind !== 'component';
  // Seconds since the orientation question went out, for the clock in its button.
  const waited = useWaited(busy === 'orient');

  // Which of those pictures somebody has explicitly put in or left out — only that, the rest
  // being derived below. See orientShots.
  const shotChoices = useOrientShots(docId ?? '');
  // ONE ROW PER PICTURE: the photograph, this part's crops on it, and whether it goes.
  //
  // Built once because three things read it and they must not disagree — what the call sends,
  // what the badge draws, and what the prompt's own numbering counts. It was three separate
  // maps over `productShots`, which was fine while every picture went; the moment one can be
  // left out, "picture 3" in the text and "the third tile" stop being the same thing.
  const shots = useMemo(
    () => productShots.map((shot) => {
      const crops = myCrops.filter((c) => c.url === shot.url);
      return {
        shot,
        crops,
        // Somebody's answer where they gave one, the derived default where they did not: a
        // picture with nothing left standing on it opens excluded.
        on: partAt == null || shotIsOn(shotChoices, partAt, shot.url, crops),
      };
    }),
    [productShots, myCrops, shotChoices, partAt],
  );
  // What actually goes up, in the order the prompt numbers it.
  const sending = useMemo(() => shots.filter((s) => s.on), [shots]);
  const maskedShots = () => Promise.all(sending.map(({ shot, crops }) =>
    paintMaskedShot(pictureSrc({ source: 'listing', imageId: shot.id }), crops.filter((c) => !c.off))
      .then((dataUrl) => ({ dataUrl, view: shot.view, masked: showsPart(crops) }))));
  // Which tile a URL in the badge belongs to — the strip draws every candidate, sent or not,
  // so this cannot count within `sending`.
  const shotAt = (url: string) =>
    shots.findIndex(({ shot }) => url.endsWith(`/listing-image/${shot.id}`));

  // WHICH WAY ROUND THIS MESH GOES, decided against those photographs and applied.
  //
  // The one control here that changes the mesh without anybody choosing what it does. That
  // is the nature of the question — "which of these six sides faces the front" has one
  // right answer and it is not a preference — and it is why the answer lands in the history
  // like every other edit, with the two faces it was told written on it and an Undo under
  // it. Nothing is decided that cannot be taken back by one press.
  //
  // A MESH ALREADY THE RIGHT WAY ROUND IS NOT AN ERROR. Most parts are, and the run says so
  // rather than filing an identity turn: what comes back is the same mesh, untouched, and a
  // line saying it was looked at.
  const orient = async () => {
    if (busy) return;
    setBusy('orient');
    setProblems([]);
    setOrientNow(null);
    // AND THE VIEWER'S FACE PLATES GO QUIET for the length of the call. Clearing the live
    // answer above is not enough: `orientSaid` falls back to the last orient edit, so the
    // previous run's words stay written on the part while the new question is out. See
    // orientAnswers.
    setOrientRunning(job.id, true);
    try {
      // THE SIX PICTURES FIRST, HERE. They are made in this browser out of the mesh's own
      // file — see meshAxisViews — and they exist nowhere else, so they go up with the
      // question. This is the same work the prompt badge does on hover, done again rather
      // than shared: it is a few hundred milliseconds, and holding six data URLs in this
      // component so a hover could hand them to a press is state that can be stale.
      const { views, seconds } = await renderAxisViews(meshSrc(job.id, meshStamp(job)));
      // And the photographs with this part drawn on them — the same picture the badge shows,
      // minus every mask somebody has turned off. See maskedShot.
      // `turned` comes back with it and is not kept: whether the mesh moved is visible in
      // the viewer, and the badge says what the model answered rather than how that compared
      // with the labels the file already had.
      const { job: done, orient: said } =
        await orientJob(job.id, solo ? [] : await maskedShots(), views, seconds, kind);
      onChanged(done);
      setOrientNow(said);
    } catch (e) {
      setProblems([falError(e, t('notice.3d.orientFailed'))]);
    } finally {
      setBusy(null);
      setOrientRunning(job.id, false);
    }
  };

  // A transformation that was WRITTEN for this mesh, applied: one busy key, the row patched
  // with what came back, the complaint shown if it failed.
  const runWritten = async (code: string, name: string, proposalId: number) => {
    if (busy || !code) return;
    setBusy('script');
    setProblems([]);
    try {
      onChanged(await runScript(job.id, code, name, proposalId));
      // The row says so from now on: five proposals and one of them is the geometry.
      setProposals((list) => list.map((x) => (x.id === proposalId
        ? { ...x, appliedAt: new Date().toISOString() } : x)));
    } catch (e) {
      // The complaints when the server refused it — "the result is not a solid: its open
      // edges went from 1324 to 2956" is the whole of what somebody needs to ask again
      // with, and it is the same shape a refused repair comes back in.
      const said = repairComplaints(e);
      setProblems(said.length ? said : [falError(e, t('notice.3d.scriptFailed'))]);
    } finally {
      setBusy(null);
    }
  };

  const ask = async () => {
    if (busy || !instruction.trim()) return;
    setBusy('advise');
    setProblems([]);
    setAdvice(null);
    try {
      const got = await adviseRepair(job.id, instruction.trim(), picked, basedOn?.id);
      setAdvice(got);
      setProposals((list) => [got.proposal, ...list]);
      setOpenId(got.proposal.id);
      // The estimate learns from the call that just landed, without asking the server for
      // it again: one more sample against however many it already had.
      setAverage((was) => {
        const took = got.proposal.elapsedSeconds;
        if (took == null) return was;
        return was == null ? took : Math.round((was * 3 + took) / 4 * 10) / 10;
      });
    } catch (e) {
      setProblems([falError(e, t('notice.3d.adviceFailed'))]);
    } finally {
      setBusy(null);
      // The dialog stays up for the half minute the model takes, spinner on its own
      // button, and closes when there is something to see. It shut as the call went out
      // for a while: that left the question you had just asked with no sign of being in
      // flight anywhere, since the button that would have spun was in the dialog.
      //
      // On a failure too. What went wrong shows under the controls, and a dialog that
      // stayed open would be covering it.
      setAsking(false);
    }
  };

  // TAKE ONE CHANGE OFF THE MESH, wherever in the stack it sits. That entry alone comes
  // out: the server goes back to the state before it and does the later ones again — see
  // drop_edit, which can refuse when one of those cannot be made a second time.
  //
  // ONE ROUTE NOW, where there were two. The Undo button below only ever reached the newest
  // edit and had /revert to itself for that; dropping the newest is the same operation with
  // nothing to replay, so the button went and the route with it. Every undo in this panel is
  // a dot on the rail.
  // An index, or several at once — see the box on the rail.
  const unmake = async (index: number | number[]) => {
    if (busy) return;
    setBusy('revert');
    setProblems([]);
    try {
      onChanged(await (Array.isArray(index) ? dropEdits(job.id, index) : dropEdit(job.id, index)));
      // AND THE LIVE ORIENTATION ANSWER GOES, whatever was undone. It says which side of the
      // product each face of the mesh is, and it says it about the mesh AS IT STOOD — undo any
      // edit and the answer on screen is being read against a different object. The stored
      // ones need no such line: they are read off the edit stack, which is what just changed.
      setOrientNow(null);
      // And the history re-read, because one of its rows has just stopped being applied.
      // From the server rather than patched here: which proposal an edit came from is
      // recorded on the edit, and this side does not have that.
      void fetchProposals(job.id)
        .then((got) => {
          setProposals(got.proposals);
          setAverage(got.averageSeconds);
        })
        .catch(() => {});
      // The proposal was about the mesh that just stopped existing. Keeping it on screen
      // would offer an Apply button computed from geometry nobody is looking at any more.
      setAdvice(null);
    } catch (e) {
      setProblems([falError(e, t('notice.3d.revertFailed'))]);
    } finally {
      setBusy(null);
    }
  };

  const drop = (index: number) => unmake(index);

  const edits = editsOf(job);
  // WHAT THE ORIENTATION CALL SAID, whether it happened a second ago or last week.
  //
  // Off the mesh's own history first, because that is where a turn's reasoning is kept —
  // so coming back to this part tomorrow still finds why it was turned, without a second
  // paid call. The live answer wins while there is one: it is the only record of the case
  // that writes no edit, and after an Undo the entry is gone while the answer that
  // produced it is still the last thing anybody was told.
  const orientSaid = orientNow ?? (() => {
    const turn = [...edits].reverse().find((e) => e.op === 'orient');
    return turn ? { ...(turn as OrientAnswer), reason: turn.reason || '' } : null;
  })();
  // AND UP TO THE VIEWER, which writes it on the six faces of the part's own box. Published
  // rather than passed because the viewer is this component's sibling with a modal between
  // them — see orientAnswers, and PartViewer, which reads it.
  //
  // WHICH FACE EACH ANSWER BELONGS TO DEPENDS ON THE MESH NOT HAVING MOVED SINCE, and both
  // halves above are safe on that count: a stored answer is read off the edit stack, so an
  // undone turn is not in it, and the live one is dropped by `unmake` for the same reason.
  useEffect(() => {
    setOrientAnswer(job.id, orientSaid);
  }, [job.id, orientSaid]);
  // The proposal whose card is under the rail.
  const shownProposal = openId === null ? proposals[0] : proposals.find((x) => x.id === openId);
  return (
    <div className="flex w-full flex-col items-center gap-2">
      {/* EVERY PROPOSAL THIS MESH HAS HAD, newest first, applied or not. It was one panel
          holding the last answer in React state, and a refresh took it: what that threw
          away is a paid call to a strong model and half a minute of waiting, and the ones
          nobody applied are the half worth keeping most.

          The renders are kept with them now, per row, for the same reason the rest of the
          row is — see MeshProposalCard. */}
      {(proposals.length > 0 || edits.length > 0) && (
        <MeshProposalRail
          edits={edits}
          labelOf={(e) => editName(e, t)}
          // The colour a colour op painted, so the entry says a COLOUR happened — its name is
          // the material's, which is right and reads exactly like naming a material.
          tintOf={(e) => (e.op === 'colour' && e.color ? cssRgb(e.color) : undefined)}
          origin={{ label: t('notice.3d.origin'), when: job.createdAt }}
          proposals={proposals}
          openId={shownProposal?.id ?? null}
          onOpen={(id) => setOpenId((was) => (was === id ? -1 : id))}
          viewingAt={viewingAt}
          // The END of the trunk is not an earlier state, it is the mesh — so pressing it
          // is how you come back, and it says so by handing null up rather than a number.
          // Without this the live mesh would be fetched from a frozen address and the
          // viewer would stop showing repairs as they land.
          onView={onViewAt && ((at: number) => onViewAt(at >= edits.length ? null : at))}
          onDelete={({ meshAt, proposalId }) => {
            // A trunk dot is the state AFTER its edit, so the entry it stands for is the
            // one before it — and that entry alone is what comes out.
            if (proposalId != null) void forget(proposalId);
            else if (meshAt != null) void drop(meshAt - 1);
          }}
          onDeleteMany={(indices) => void unmake(indices)}
        />
      )}

      {/* YOU ARE LOOKING AT AN EARLIER MESH, said in the panel and not only by a ring.
          Every control below this line acts on the mesh as it STANDS — Apply lands on the
          end of the trunk wherever you happen to be looking — and a viewer showing
          something else with nothing to say so is how somebody presses a button expecting
          it to land where their eyes are. */}
      {viewingAt != null && onViewAt && (
        <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500">
          <History className="h-3.5 w-3.5 shrink-0" />
          {viewingAt > 0 && (
            <span>{t('notice.3d.viewingAfter').replace('{n}', String(viewingAt))}</span>
          )}
          <button
            type="button"
            onClick={() => onViewAt(null)}
            className="rounded-lg border border-slate-300 px-2 py-0.5 font-medium text-slate-600 transition-colors hover:border-blue-400 hover:text-blue-600"
          >
            {t('notice.3d.viewingBack')}
          </button>
        </div>
      )}

      {/* TWO BANDS, EACH UNDER THE QUESTION IT ANSWERS: how big the part is and which way
          round, and where its holes are.

          ONE ROW BEFORE, WRAPPING WHERE THE VIEWPORT HAPPENED TO BREAK. Controls in a line
          have an order and no grouping, so the break landed in a different place on every
          screen and never on a meaning. A band that is named cannot break in the wrong place,
          because the name says where it ends.

          NO SURFACE BAND AND NO MODIFICATIONS, for a part. A component's mesh is a piece cut
          out of the whole product and a piece of hardware is drawn: neither is a reconstruction
          to be closed, stripped of stray shells or re-cut by hand, and what a part is made of
          is the materials phase's. What is left is scale, orientation and holes. */}
      <div className="mx-auto flex w-full flex-col items-center gap-2.5">
        <Band title={t('notice.3d.bandShape')}>
            <Menu
              icon={rescaling
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Scaling className="h-3.5 w-3.5" />}
              label={t('notice.3d.scaling')}
              disabled={rescaling || !!busy}
              items={SCALE_AXES.map(({ axis, label }) => {
                const fit = size ? axisFit(size, dims, axis, isTrueScale(job)) : null;
                const name = t(label);
                return {
                  key: axis,
                  icon: <Scaling className="h-3.5 w-3.5" />,
                  label: (
                    <>
                      {t('notice.3d.scaleOn')} {axis.toUpperCase()}
                      <span className="font-normal">({name})</span>
                    </>
                  ),
                  // The tooltip is the whole argument for pressing it: what the mesh measures
                  // now and what it would measure after. An item that only said "scale on Y"
                  // would be asking for a decision while withholding the two numbers it turns
                  // on — and when there is nothing to scale to, this is what says so.
                  title: fit
                    ? `${name}: ${Math.round(fit.current)} mm → ${fit.measured} mm`
                    : t('notice.3d.scaleNoDim'),
                  disabled: !fit,
                  onPick: () => fit && onRescale(axis, fit),
                };
              })}
            />

            {/* WHICH WAY ROUND THE PART GOES, which nothing has ever decided.

                The six words on the box come from one table — +y is the back, +z the top — and
                the frame they are true of is the one the mesh was made in, which came from the
                viewpoint of the picture it was made from. So "Back" is a face of
                a bounding box and not a statement about the furniture, and the measurements the
                manual states are in the product's frame while the mesh's are in the picture's.

                A MENU, because there are two ways to answer it. Put it to the model, which is
                what this always was; or turn the part by hand, which is what somebody who is
                already looking at a leg lying on its side wants — the answer without the minute
                of inference and the paragraph about why. Both end in the same edit.

                THE MODEL'S HALF NEEDS SOMETHING TO MATCH AGAINST, and the hand's does not. The
                whole menu used to disappear with the photographs — for hardware, for a notice
                whose listing nobody has read, and now for any part Phase 1 could not find in a
                classified picture, which is a great many of them. That took the BY-HAND turn away
                with it, which needs no photograph at all: somebody looking at a leg lying on its
                side does not need a chair to compare it to.

                So the menu stands and the model's half is disabled with a reason. A greyed row
                saying what is missing is a fact about this part; a menu that is not there is a
                feature nobody knows exists.

                NOT FOR A WRITTEN MODEL. GPT-6 Astra builds in a stated frame — Z up, the
                product's front at -Y, hardware head up along -Z — and a component is cut out of
                that product, so there is no picture's viewpoint left to undo. The menu stays for
                the reconstructions and CAD uploads, which carry whatever frame they came in. */}
            {!isWrittenRun(job) && (
            <div className="flex items-center gap-1">
              <Menu
                icon={busy === 'orient'
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Compass className="h-3.5 w-3.5" />}
                label={(
                  <>
                    {t('notice.3d.orient')}
                    {/* HOW LONG IT HAS BEEN GOING, in the button. This is a strong model
                        reading ten pictures and it takes the better part of a minute; a
                        spinner alone says only that something is happening, which after twenty
                        seconds is the question rather than the answer. The same clock the
                        repair dialog counts on — see waitClock, which exists because a second
                        copy of a ticker is where one of them stops matching. */}
                    {busy === 'orient' && (
                      <span className="ml-1 tabular-nums font-normal">({clock(waited)})</span>
                    )}
                  </>
                )}
                disabled={!!busy}
                items={[
                  {
                    key: 'ask',
                    icon: <Compass className="h-3.5 w-3.5" />,
                    label: t('notice.3d.orientAsk'),
                    // WHY YOU CANNOT, where you cannot: no photograph of the assembled thing
                    // carries this part, so there is nothing for the six renders to be matched
                    // against. It names the phase that would fix it rather than stating the
                    // absence — the crops are found in Phase 1 and can be re-run per part.
                    //
                    // HARDWARE IS NEVER MISSING ANYTHING. A screw belongs to no product, so it is
                    // asked about itself from the six renders alone and no photograph would add to
                    // that — see mesh_orient.KINDS. Gated on the kind and not on the picture count,
                    // because those two now agree only by accident: a component whose crops nobody
                    // has found has no pictures either, and it genuinely cannot be asked.
                    disabled: !solo && !sending.length,
                    title: solo ? t('notice.3d.orientHintSolo')
                      : sending.length ? t('notice.3d.orientHint')
                        : t('notice.3d.orientNoShots'),
                    onPick: () => void orient(),
                  },
                  {
                    key: 'hand',
                    icon: <RotateCw className="h-3.5 w-3.5" />,
                    label: t('notice.3d.orientByHand'),
                    title: t('notice.3d.turnHint'),
                    onPick: () => setTurning(true),
                  },
                ]}
              />
              {/* WHAT IT WOULD SEND, pictures and all — the six renders of this mesh and the
                  photographs they are matched against. The one control on this row whose
                  inputs are chosen for you rather than picked on screen, so the badge is the
                  only place that says what they are.

                  Only where there is a call to preview. With no photograph of this part the
                  menu row above is disabled and says why, and a badge beside it would open on
                  a question nobody can ask.

                  HARDWARE HAS A CALL TO PREVIEW WITH NO PHOTOGRAPHS AT ALL, and it is the one
                  that most needs reading: the whole question is the six renders and a paragraph
                  about which side a part rests on, so the badge opens on the words and the six
                  tiles alone. The strip below simply has no photographs in it. */}
              {(solo || productShots.length > 0) && (
                <CallBadge
                  promptLabel={t('notice.3d.orientPrompt')}
                  // The photographs are the page's own — it is the page that draws the part on
                  // them — so the server is asked only for the words, and told what each picture
                  // WILL be. What comes back captions them.
                  load={() => orientPrompt(job.id, sending.map(({ shot, crops }) => ({
                    view: shot.view,
                    masked: showsPart(crops),
                  })), kind).then(({ prompt, labels, model }) => {
                    // THE LABELS BELONG TO WHAT IS SENT, and the strip draws more than that.
                    // The server writes one line per photograph it was told about, in order, so
                    // an excluded picture consumes none of them — it takes a word saying it is
                    // out instead. Counted along the row rather than by index, since the two
                    // lists only line up while nothing is excluded.
                    let sent = 0;
                    return {
                      prompt,
                      model,
                      // THE PHOTOGRAPH ITSELF, with its crops drawn OVER it rather than burnt in.
                      // The zoom has to be able to strike one through, and a mask baked into a
                      // picture cannot be clicked — so the badge shows the stored picture with the
                      // boxes on top, and only what is sent has them painted on.
                      //
                      // EVERY CANDIDATE, INCLUDING THE ONES LEFT OUT. The strip is the only place
                      // an excluded picture can be put back, so it stays in it — greyed, struck
                      // through, and saying so.
                      urls: shots.map(({ shot, on }) => {
                        // Its place in the prompt, 1-based, or none for a picture left out.
                        const n = on ? ++sent : 0;
                        return {
                          url: pictureSrc({ source: 'listing', imageId: shot.id }),
                          label: n ? labels[n - 1] : t('notice.3d.orientLeftOut'),
                          // WHICH PICTURE OF THE TEXT THIS IS. It was the viewpoint alone —
                          // FRONT, BACK — which worked while every photograph in the set had a
                          // viewpoint and no two shared one. Both stopped being true the day a
                          // picture got in for having the part outlined on it: a detail shot and
                          // an in-situ shot are both "Other", and the prompt describes them as
                          // two different numbered pictures. So the number leads, which is what
                          // the text actually calls them.
                          caption: n
                            ? `${n} · ${directionLabel(shot.view)}`
                            : directionLabel(shot.view),
                          off: !on,
                        };
                      }),
                    };
                  })}
                  // WHERE THE PART IS ON EACH ONE, asked for on every render rather than handed
                  // over once — clicking a mask has to grey it there and then, and the crops
                  // live in a store this component is subscribed to.
                  boxesFor={(url) => {
                    const at = shotAt(url);
                    return at < 0 ? undefined : shots[at].crops.map((c) => ({
                      box: c.bbox, off: c.off, outlines: cropRings(c), label: partName,
                    }));
                  }}
                  // Struck through HERE is struck through in the parts table and in what the
                  // call sends: one flag, written through the store that owns it.
                  // WHAT THE WORDS DEPEND ON: which masks are still standing. Striking one
                  // through changes the prompt ("with this part outlined in green") and the
                  // caption under that picture, and both come from the server — so it is asked
                  // again. See usePromptCall, which refetches rather than remounting because the
                  // press that changes this happens inside its own zoom.
                  //
                  // AND WHICH PICTURES GO AT ALL, for the same reason and one stronger: leaving
                  // one out changes how many photographs the prompt describes and what every one
                  // after it is numbered, which is most of the text this panel is showing.
                  revision={shots.map(({ shot, crops, on }) =>
                    `${shot.id}${on ? '+' : '-'}${crops.map((c) => (c.off ? '0' : '1')).join('')}`)
                    .join('|')}
                  onToggleBox={docId && partAt != null ? (url, at) => {
                    const found = shotAt(url);
                    if (found >= 0) toggleListingCrop(docId, partAt, shots[found].shot.url, at);
                  } : undefined}
                  // THE WHOLE PICTURE IN OR OUT, from the word under it. A photograph the part
                  // cannot be made out in is not weak evidence: the call matches six renders
                  // against these, and one that shows nothing of this part is something for a
                  // render to be matched to anyway.
                  onTogglePicture={docId && partAt != null ? (url) => {
                    const found = shotAt(url);
                    if (found >= 0) {
                      setOrientShot(docId, partAt, shots[found].shot.url, !shots[found].on);
                    }
                  } : undefined}
                  // The six renders, behind the words — made here, out of the same file the
                  // press will use, so what the badge shows is what would be sent. Still its
                  // own loader: they are a mesh parse and the text is ready at once.
                  //
                  // EACH ONE SAYS WHICH MESH AND WHICH SIDE. Opened large they are brown shapes
                  // on white — a leg down its own length is the same picture from four of the
                  // six angles — and a part with three reconstructions has three of these sets.
                  // Neither question is answerable from the picture, and both are the reason to
                  // open one.
                  more={() => renderAxisViews(meshSrc(job.id, meshStamp(job)))
                    .then(({ views }) => views.map((v, i) => ({
                      url: v.dataUrl,
                      // THE LETTER, under the tile — the only name the model has for this
                      // picture, and the one its answer comes back in.
                      caption: 'ABCDEF'[i] ?? '?',
                      // AND THE LETTER IT GOES UP AS. The model is shown these six lettered and
                      // never told which side is which — see mesh_orient.VIEW_TOKENS — so its
                      // answer comes back as "front: A", and this is the only place a person can
                      // read that against the picture it is about.
                      label: t('notice.3d.viewOfMesh')
                        .replace('{t}', 'ABCDEF'[i] ?? '?')
                        .replace('{n}', String(job.id))
                        .replace('{side}', t(`notice.assembly.face${v.name.charAt(0).toUpperCase()}${v.name.slice(1)}`).toLowerCase()),
                    })))}
                  // AND WHY IT SAID SO, on the same badge's second row. The turn itself is
                  // visible — the part swings — and what is not is which photograph was matched
                  // to which side, which is the only part of this anybody can disagree with.
                  answer={orientSaid ? {
                    label: t('notice.3d.orientThinking'),
                    content: (
                      // Centred in the panel for the reason the prompt's is: the reading width
                      // is deliberately narrower than the panel, and a column of it pinned to
                      // the left edge is a hole beside the words rather than a margin round them.
                      <div className="mx-auto max-w-md space-y-3">
                        {/* WHAT IT ANSWERED, in the words it answered in: each of the six lettered
                            views, and the side of the product it shows. The letters are the ones
                            under the pictures in the prompt beside this.

                            NOTHING ABOUT WHAT THE FILE CALLED THEM BEFORE. The model is shown A to
                            F and is never told the mesh's own names for those sides — that is the
                            whole point of the letters — so a line reading "front: the side it
                            called front" was the answer resolved against the arbitrary labelling
                            it was designed not to see, and it read as though the model had been
                            told the orientation in advance. Same for the banner above it, which
                            said "turned" or "already the right way round": both are the answer
                            MINUS the old state, and the turn itself is visible in the viewer.

                            AND WHICH OF THEM DOES NOT ADD UP. Six independent answers can
                            contradict each other where two perpendicular ones could not, so this
                            is no longer a block of text — see OrientViews, which draws the
                            disagreements and explains them. */}
                        <OrientViews answer={orientSaid} />
                        {tipSection(t('notice.cat.reasoningTitle'),
                          orientSaid.reason || t('notice.cat.noReasoning'))}
                        {/* WHAT IT REPLIED, under its own account of itself. The pair every
                            other badge in this app shows, for the reason that one does: when the
                            answer is a face nobody expected, the difference between the prose
                            and the JSON is where it went wrong. */}
                        {orientSaid.raw && tipSection(t('notice.cat.rawOutputTitle'), orientSaid.raw)}
                        {/* AND WHERE THE MINUTE WENT. Six Blender renders, then the call — the
                            two halves of the wait, measured where they happen. It is the answer
                            to the only question anybody asks about this button, and without it
                            the guess is always the prompt, which is never the reason. */}
                        {orientSaid.modelSeconds != null && tipSection(
                          t('notice.3d.orientTimeTitle'),
                          [
                            `${t('notice.3d.orientTimeRenders')} ${orientSaid.renderSeconds ?? 0}s`,
                            `${modelLabel(orientSaid.model || '') || t('notice.3d.orientTimeCall')} ${orientSaid.modelSeconds}s`,
                          ].join('\n'),
                        )}
                      </div>
                    ),
                  } : undefined}
                />
              )}
            </div>
            )}
        </Band>
      </div>

      {asking && (
        <RepairAskModal
          instruction={instruction}
          onInstruction={setInstruction}
          groups={groups}
          picked={picked}
          onPicked={setChosen}
          busy={!!busy}
          onContext={() => fetchAdviceContext(job.id, picked, basedOn?.id)}
          contextKey={`${picked.map((r) => `${r.source}-${r.imageId}`).join(',')}|${basedOn?.id ?? ''}`}
          followingOn={[...edits].reverse().find((e) => e.op === 'script')?.name || ''}
          refining={basedOn?.name || ''}
          averageSeconds={average}
          onAsk={() => void ask()}
          onClose={() => setAsking(false)}
        />
      )}

      {/* THE SAME QUESTION, ANSWERED BY HAND — and asked in the same words the model answers
          in: two of the mesh's faces, and the side of the product each one really is. See
          MeshRotateModal, which argues why that beats six buttons naming axes. */}
      {turning && (
        <MeshRotateModal jobId={job.id} onChanged={onChanged} onClose={() => setTurning(false)} />
      )}

      {shownProposal && (
        <MeshProposalCard
          key={shownProposal.id}
          proposal={shownProposal}
          open={shownProposal.id === advice?.proposal.id}
          sawDrawing={shownProposal.id === advice?.proposal.id ? advice.sawDrawing : undefined}
          busy={busy === 'script'}
          onApply={() => void runWritten(shownProposal.code, shownProposal.name, shownProposal.id)}
          onRefine={() => { setBasedOn(shownProposal); setAsking(true); }}
          onForget={() => void forget(shownProposal.id)}
        />
      )}

      {problems.map((p2) => (
        <span key={p2} className="text-center text-[11px] text-rose-400">{p2}</span>
      ))}
    </div>
  );
}
